import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AppPageService, CustomerFacade, MessageService, SsoAccountData, SsoAccountFacade } from "common";
import { DocuSignSigningFacade } from "../domain/+state/docusign-signing.facade";
import {
  DocuSignReturnEvent,
  SigningStateData,
  SigningSubject,
  SigningSubjectKind,
  signAgreementRoute,
} from "../domain/models/docusign-signing.model";
import { PostSignNavigationService } from "../post-sign-navigation.service";
import { LoanData } from "../../loan/domain/models/loan.model";
import { OfferBundleInfo } from "../../agreement/agreement.data";
import { ApplicationData } from "../../application/application.model";

/** Where DocuSign returns the signer, on the `?event=<outcome>` appended to the return URL. */
@UntilDestroy()
@Component({
  selector: "ifp-docusign-return",
  templateUrl: "./docusign-return.component.html",
  styleUrls: ["./docusign-return.component.scss"],
  standalone: false,
})
export class DocuSignReturnComponent implements OnInit {
  /** Shown while the signature is being confirmed. */
  waiting = false;

  /** Set when the wait ran out — the signature is on its way, just not recorded yet. */
  finalising = false;

  /** Set when DocuSign reported the signer declined. */
  declined = false;

  /** Set when this signer is done but a co-owner has still to sign. */
  awaitingCoSigners = false;

  /** Who else the agreement is waiting on, when {@link awaitingCoSigners} is set. */
  outstandingSignerNames: string[] = [];

  private data: DocuSignReturnComponentData;

  /** The loan, or the MSA application, being signed; from the route. */
  private subject: SigningSubject;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly facade: DocuSignSigningFacade,
    private readonly postSignNavigation: PostSignNavigationService,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly appPageService: AppPageService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: DocuSignReturnComponentData) => (this.data = data));

    this.subject = this.resolveSubject();

    const event = this.route.snapshot.queryParamMap.get("event") as DocuSignReturnEvent | null;

    switch (event) {
      case DocuSignReturnEvent.SigningComplete:
        this.onSigningComplete();
        break;

      case DocuSignReturnEvent.Decline:
        // Nothing for the portal to undo: the Connect webhook records the decline.
        this.declined = true;
        break;

      case DocuSignReturnEvent.ViewingComplete:
        // The envelope was already completed when it was opened, so there was nothing to sign.
        this.goToDashboard();
        break;

      default:
        // No signature was collected; the sign step mints a fresh URL.
        this.returnToSignStep();
        break;
    }
  }

  /** An MSA signer goes back to where they came from (my.levelesq), when they came from somewhere. */
  goToDashboard(): void {
    if (this.subject.kind === SigningSubjectKind.Application && this.appPageService.backToIfPossible(true)) {
      return;
    }

    this.customerFacade.redirectToDashboard();
  }

  trackByName(_index: number, name: string): string {
    return name;
  }

  returnToSignStep(): void {
    void this.router.navigate([signAgreementRoute(this.subject)]);
  }

  private resolveSubject(): SigningSubject {
    if (this.route.snapshot.data?.signingSubjectKind === SigningSubjectKind.Application) {
      return {
        kind: SigningSubjectKind.Application,
        id: this.data?.application?.id ?? Number(this.route.snapshot.paramMap.get("appId")),
      };
    }

    return {
      kind: SigningSubjectKind.Loan,
      id: this.data?.loan?.id ?? Number(this.route.snapshot.paramMap.get("accountId")),
    };
  }

  private onSigningComplete(): void {
    this.waiting = true;

    // Cleared before the poll: both caches are keyed on signing state.
    this.ssoAccountFacade.clearSsoAccountCache();
    this.customerFacade.clearCustomerCache();

    this.facade
      .waitForRecordedSignature$(this.subject)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.waiting = false;

          if (!result.signatureRecorded) {
            // Not an error: DocuSign has the signature, RYNO is only behind on recording it.
            this.finalising = true;
            return;
          }

          // This signer is done; the agreement may not be.
          if (!DocuSignSigningFacade.isExecuted(result.state)) {
            this.outstandingSignerNames = result.state?.outstandingSignerNames ?? [];
            this.awaitingCoSigners = true;
            return;
          }

          if (this.subject.kind === SigningSubjectKind.Application) {
            // Where the in-house MSA signing sends the signer: back to my.levelesq, or the dashboard.
            this.goToDashboard();
            return;
          }

          this.navigateOnward(result.state);
        },
        error: (error: unknown) => {
          this.waiting = false;
          this.finalising = true;
          this.messageService.error(error);
        },
      });
  }

  /** Hands off to the same navigation the in-house ceremony uses. */
  private navigateOnward(state: SigningStateData | null): void {
    const loan = this.data?.loan;

    this.postSignNavigation
      .navigateAfterSigning$({
        // The server's answer, not the SSO snapshot's: the snapshot predates the recorded signature.
        signedLoanId: state?.signedLoanId ?? loan?.id,
        sourceLoanId: loan?.id,
        entityId: loan?.entityId,
        offerBundleId: this.data?.offerBundlesInfo?.offerBundleId,
        bankIntegrationRequired: false,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        // Without an error handler a failing refresh leaves the template with no block to render.
        error: (error: unknown) => {
          this.finalising = true;
          this.messageService.error(error);
        },
      });
  }
}

export interface DocuSignReturnComponentData {
  currentSsoAccount: SsoAccountData;
  /** Set on a loan's return route. */
  loan?: LoanData;
  /** Set on an MSA application's return route. */
  application?: ApplicationData;
  offerBundlesInfo: OfferBundleInfo;
}
